import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import { useDebounce } from '@/utils/useDebounce'
import { Address, erc20Abi, formatUnits, parseUnits } from 'viem'
import BigNumber from 'bignumber.js'
import {
  useAccount,
  useConfig,
  useReadContract,
  useReadContracts,
  useWriteContract,
} from 'wagmi'
import { useWaitForTransactionReceiptUniversal } from '@/domain/hooks/useWaitForTransactionReceiptUniversal'
import { useQueryClient } from '@tanstack/react-query'
import { ACCOUNT_LENS_ADDRESS, E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { EVAULT_SIMULATOR_ADDRESS } from '@/config/consts'
import { evaultSimulatorABI } from 'abis/evm/eVaultSimulator'
import { invalidateBalancesCache } from '@/domain/cache'
import { getPositionLabel } from '@/domain/euler/positions/usePositionName'
import { usePositionNames } from '@/domain/state'
import { useUserCollateralState } from '@/domain/euler/rebalance'
import { getSubAccountAddress, FALLBACK_SUBACCOUNT_RANGE } from '@/domain/euler/shared/subAccounts'
import { useSubaccountStore, useSubaccountControllers } from '@/domain/euler/subaccounts'
import { isUserRejection } from '@/lib/errors'
import { useActionTimeout } from '@/features/actions/logic/useActionTimeout'
import { getVaultMarket } from '@/features/evault-details/data/products'
import { accountLensAbi } from 'abis/evm/accountLens'
import {
  type TransactionItems,
  type TransferTransactionItem,
  TransactionLabel,
  useSimulateEVCBatchCall,
} from '@eulerxyz/wagmi-evc'

const MAX_UINT256 = 2n ** 256n - 1n

interface UseETransferDialogParams {
  vaultAddress: Address
  underlyingAsset: Address
  assetSymbol: string
  assetDecimals: number
  logoURI?: string
  priceUSD: number
  /** Current balance formatted (underlying asset amount) */
  currentBalance: number
  /** Source sub-account ID */
  sourceSubAccountId: number
}

export interface SubAccountOption {
  id: number
  address: Address
  label: string
  /** Whether this sub-account has an active position (has collateral or debt) */
  hasPosition: boolean
  /** Whether this is the "New Position" option */
  isNew: boolean
  /** Debt token symbols for this position */
  debtSymbols: string[]
  /** Total debt USD */
  totalDebtUsd: number
  /** Total collateral USD */
  totalCollateralUsd: number
  /** Health factor */
  healthFactor: number | null
  /** Market name */
  marketName?: string
}

export interface UseETransferDialogResult {
  token: Token | undefined
  transferAmount: string
  setTransferAmount: (amount: string) => void
  maxTransferable: number
  selectedDestination: SubAccountOption | null
  setSelectedDestination: (dest: SubAccountOption | null) => void
  destinations: SubAccountOption[]
  isDestinationsLoading: boolean
  canTransfer: boolean
  transferValueUSD: number
  sourceSubAccountLabel: string
  isDebouncing: boolean
  isSimulating: boolean
  isWriting: boolean
  isWaiting: boolean
  isConfirmed: boolean
  hasSimulationData: boolean
  displayError: Error | null
  simulationError: Error | null
  handleExecute: () => void
  isSuccess: boolean
  isTimedOut: boolean
  /** Current health factor of the source position */
  currentSourceHF: number
  /** Predicted health factor after transfer */
  newSourceHF: number
  /** Whether HF simulation is in progress */
  isSimulatingSourceHF: boolean
  /** Whether source position has debt (HF is relevant) */
  sourceHasDebt: boolean
}

export function useETransferDialog({
  vaultAddress,
  underlyingAsset,
  assetSymbol,
  assetDecimals,
  logoURI,
  priceUSD,
  currentBalance,
  sourceSubAccountId,
}: UseETransferDialogParams): UseETransferDialogResult {
  const [transferAmount, setTransferAmount] = useState('')
  const [selectedDestination, setSelectedDestination] = useState<SubAccountOption | null>(null)
  const [isSuccess, setIsSuccess] = useState(false)
  const { address: userAddress } = useAccount()
  const config = useConfig()
  const queryClient = useQueryClient()
  const chainId = E_LEND_CHAIN_ID
  const { getPositionName } = usePositionNames()

  // Debounce the transfer amount
  const [keyCounter, setKeyCounter] = useState(0)
  const prevTransferAmountRef = useRef(transferAmount)

  useEffect(() => {
    if (prevTransferAmountRef.current !== transferAmount) {
      prevTransferAmountRef.current = transferAmount
      setKeyCounter((prev) => prev + 1)
    }
  }, [transferAmount])

  const { debouncedValue: debouncedTransferAmount, isDebouncing } = useDebounce(
    transferAmount,
    `transfer-${keyCounter}`,
    { delay: 300 },
  )

  // Source sub-account address
  const sourceSubAccountAddress = useMemo(() => {
    if (!userAddress) return undefined
    return getSubAccountAddress(userAddress, sourceSubAccountId)
  }, [userAddress, sourceSubAccountId])

  const sourceSubAccountLabel = getPositionLabel(sourceSubAccountId, getPositionName(userAddress, sourceSubAccountId))

  // Fetch user's vault shares balance for the source sub-account
  const { data: userSharesRaw } = useReadContract({
    address: vaultAddress,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: sourceSubAccountAddress ? [sourceSubAccountAddress] : undefined,
    chainId,
    query: {
      enabled: !!sourceSubAccountAddress,
      refetchInterval: 10_000,
    },
  })

  // Discover sub-accounts via controllers
  const { activeSubaccountIds } = useSubaccountStore()

  const idsToQuery = useMemo(() => {
    const fallbackIds = Array.from({ length: FALLBACK_SUBACCOUNT_RANGE }, (_, i) => i)
    return [...new Set([...fallbackIds, ...activeSubaccountIds])]
  }, [activeSubaccountIds])

  const { subaccountControllers, isLoading: isControllersLoading } = useSubaccountControllers({
    owner: userAddress,
    subaccountIds: idsToQuery,
    chainId,
    enabled: !!userAddress,
  })

  // Also discover sub-accounts with deposits (savings-only positions without debt).
  // Reuses useUserCollateralState — data is already cached from the dashboard, no extra RPC.
  const { state: collateralState, isLoading: isCollateralStateLoading } = useUserCollateralState({
    userAddress,
    chainId,
    enabled: !!userAddress,
  })

  // Sub-account IDs that have actual vault balances (deposits)
  const subAccountsWithDeposits = useMemo(() => {
    if (!collateralState) return new Set<number>()
    const ids = new Set<number>()
    for (const c of collateralState.collaterals) {
      ids.add(c.subAccountId)
    }
    return ids
  }, [collateralState])

  // Active sub-accounts: those with controllers (debt) OR deposits (savings), excluding source
  const activeSubAccounts = useMemo(() => {
    if (!userAddress) return []

    const activeIds = new Set<number>()
    const controllerMap = new Map<number, Address | null>()

    for (const [id, controllers] of subaccountControllers) {
      if (id === sourceSubAccountId) continue
      if (controllers.length > 0) {
        activeIds.add(id)
        controllerMap.set(id, controllers[0]!)
      }
    }

    // Add savings-only positions (deposits but no controller)
    for (const id of subAccountsWithDeposits) {
      if (id === sourceSubAccountId) continue
      if (!activeIds.has(id)) {
        activeIds.add(id)
        controllerMap.set(id, null)
      }
    }

    return Array.from(activeIds)
      .sort((a, b) => {
        if (a === 0) return -1
        if (b === 0) return 1
        return a - b
      })
      .map((id) => ({
        id,
        controller: controllerMap.get(id) ?? null,
        subAccountAddress: getSubAccountAddress(userAddress, id),
      }))
  }, [userAddress, subaccountControllers, subAccountsWithDeposits, sourceSubAccountId])

  // Source position's controller (liability vault) — needed for HF simulation
  const sourceController = useMemo((): Address | null => {
    const controllers = subaccountControllers.get(sourceSubAccountId)
    return controllers && controllers.length > 0 ? controllers[0]! : null
  }, [subaccountControllers, sourceSubAccountId])

  // Check if the vault being transferred is actual collateral backing debt (not savings).
  // Mirrors "Your Positions" logic: must be enabled as collateral AND same market as controller.
  const isTransferringCollateral = useMemo(() => {
    if (!sourceController) return false
    if (!collateralState) return false

    const enabledForSource = collateralState.enabledCollaterals.get(sourceSubAccountId) || []
    const isEnabled = enabledForSource.some((c) => c.toLowerCase() === vaultAddress.toLowerCase())
    if (!isEnabled) return false

    const controllerMarketKey = getVaultMarket(sourceController)?.key
    if (!controllerMarketKey) return false
    const vaultMarketKey = getVaultMarket(vaultAddress)?.key
    return vaultMarketKey === controllerMarketKey
  }, [sourceController, collateralState, sourceSubAccountId, vaultAddress])

  const sourceHasDebt = isTransferringCollateral

  // Simulate health factor for source position after transfer
  const transferAmountRaw = useMemo(() => {
    if (!debouncedTransferAmount || Number(debouncedTransferAmount) <= 0) return 0n
    try {
      return parseUnits(debouncedTransferAmount, assetDecimals)
    } catch {
      return 0n
    }
  }, [debouncedTransferAmount, assetDecimals])

  // Get current HF from simulator — only when transferring actual collateral (not savings)
  const { data: currentHFData, isLoading: isLoadingCurrentHF } = useReadContracts({
    contracts:
      isTransferringCollateral && sourceController && sourceSubAccountAddress
        ? [
            {
              address: EVAULT_SIMULATOR_ADDRESS,
              abi: evaultSimulatorABI,
              functionName: 'getHealthFactor' as const,
              args: [sourceController, sourceSubAccountAddress, true] as const,
              chainId,
            },
          ]
        : [],
    query: {
      enabled: isTransferringCollateral && !!sourceController && !!sourceSubAccountAddress,
      staleTime: 30_000,
    },
  })

  // Simulate new HF after withdrawing the transfer amount — only for actual collateral
  const { data: newHFData, isLoading: isSimulatingNewHF } = useReadContracts({
    contracts:
      isTransferringCollateral && sourceController && sourceSubAccountAddress && transferAmountRaw > 0n
        ? [
            {
              address: EVAULT_SIMULATOR_ADDRESS,
              abi: evaultSimulatorABI,
              functionName: 'simulateHealthFactorAfterWithdraw' as const,
              args: [sourceController, sourceSubAccountAddress, vaultAddress, transferAmountRaw] as const,
              chainId,
            },
          ]
        : [],
    query: {
      enabled: isTransferringCollateral && !!sourceController && !!sourceSubAccountAddress && transferAmountRaw > 0n,
      staleTime: 10_000,
    },
  })

  const currentSourceHF = useMemo(() => {
    if (!currentHFData || currentHFData.length === 0) return Number.POSITIVE_INFINITY
    const result = currentHFData[0]
    if (result?.status === 'success' && result.result !== undefined) {
      const raw = result.result as bigint
      if (raw >= MAX_UINT256 - 1n) return Number.POSITIVE_INFINITY
      return Number(formatUnits(raw, 18))
    }
    return Number.POSITIVE_INFINITY
  }, [currentHFData])

  const newSourceHF = useMemo(() => {
    if (!newHFData || newHFData.length === 0) return Number.POSITIVE_INFINITY
    const result = newHFData[0]
    if (result?.status === 'success' && result.result !== undefined) {
      const raw = result.result as bigint
      if (raw >= MAX_UINT256 - 1n) return Number.POSITIVE_INFINITY
      return Number(formatUnits(raw, 18))
    }
    return Number.POSITIVE_INFINITY
  }, [newHFData])

  const isSimulatingSourceHF = isLoadingCurrentHF || isSimulatingNewHF

  // Sub-accounts with controllers (subset used for Account Lens enrichment)
  const subAccountsWithControllers = useMemo(
    () => activeSubAccounts.filter((s): s is typeof s & { controller: Address } => s.controller !== null),
    [activeSubAccounts],
  )

  // Build debt symbol map from collateralState.borrows (has correct underlying asset symbol)
  const debtSymbolMap = useMemo(() => {
    const map = new Map<number, string>()
    if (!collateralState) return map
    for (const b of collateralState.borrows) {
      map.set(b.subAccountId, b.symbol)
    }
    return map
  }, [collateralState])

  // Batch-read Account Lens for health factor
  const accountLensContracts = useMemo(
    () =>
      subAccountsWithControllers.map(({ subAccountAddress, controller }) => ({
        address: ACCOUNT_LENS_ADDRESS,
        abi: accountLensAbi,
        functionName: 'getAccountInfo' as const,
        args: [subAccountAddress, controller] as const,
        chainId,
      })),
    [subAccountsWithControllers, chainId],
  )

  const { data: accountLensData } = useReadContracts({
    contracts: accountLensContracts,
    query: {
      enabled: accountLensContracts.length > 0,
      staleTime: 30_000,
    },
  })

  // Parse enrichment data into a map
  const enrichmentMap = useMemo(() => {
    const map = new Map<
      number,
      { debtSymbol: string | null; healthFactor: number | null; totalCollateralUsd: number; totalDebtUsd: number }
    >()

    for (let i = 0; i < subAccountsWithControllers.length; i++) {
      const { id } = subAccountsWithControllers[i]!

      // Debt symbol from collateralState.borrows (underlying asset symbol, e.g. "WHYPE")
      const debtSymbol = debtSymbolMap.get(id) ?? null

      // Health factor from Account Lens
      let healthFactor: number | null = null
      let totalCollateralUsd = 0
      let totalDebtUsd = 0
      if (accountLensData?.[i]?.status === 'success' && accountLensData[i]!.result) {
        const liquidityInfo = (
          accountLensData[i]!.result as {
            vaultAccountInfo: {
              liquidityInfo: {
                queryFailure: boolean
                liabilityValueLiquidation: bigint
                collateralValueLiquidation: bigint
                collateralValueRaw: bigint
              }
            }
          }
        ).vaultAccountInfo.liquidityInfo

        if (!liquidityInfo.queryFailure) {
          totalDebtUsd = Number(formatUnits(liquidityInfo.liabilityValueLiquidation, 18))
          totalCollateralUsd = Number(formatUnits(liquidityInfo.collateralValueRaw, 18))
          const collateralLiqUsd = Number(formatUnits(liquidityInfo.collateralValueLiquidation, 18))
          healthFactor = totalDebtUsd > 0 ? collateralLiqUsd / totalDebtUsd : null
        }
      }

      map.set(id, { debtSymbol, healthFactor, totalCollateralUsd, totalDebtUsd })
    }

    return map
  }, [subAccountsWithControllers, debtSymbolMap, accountLensData])

  // Build destination options — active positions + one "New Position" filling the first gap
  const destinations = useMemo((): SubAccountOption[] => {
    if (!userAddress) return []

    const options: SubAccountOption[] = []

    // Show sub-accounts with active positions (debt and/or deposits)
    for (const { id, controller } of activeSubAccounts) {
      const enrichment = enrichmentMap.get(id)
      const marketName = controller ? getVaultMarket(controller)?.name : undefined

      options.push({
        id,
        address: getSubAccountAddress(userAddress, id),
        label: getPositionLabel(id, getPositionName(userAddress, id)),
        hasPosition: true,
        isNew: false,
        debtSymbols: enrichment?.debtSymbol ? [enrichment.debtSymbol] : [],
        totalDebtUsd: enrichment?.totalDebtUsd ?? 0,
        totalCollateralUsd: enrichment?.totalCollateralUsd ?? 0,
        healthFactor: enrichment?.healthFactor ?? null,
        marketName,
      })
    }

    // "New Position" — fill the first gap in IDs (0-15 range, max 16 sub-accounts)
    // Only IDs that actually have controllers count as "used"
    const usedIds = new Set<number>([sourceSubAccountId])
    for (const { id } of activeSubAccounts) {
      usedIds.add(id)
    }

    let newId = -1
    for (let i = 0; i < FALLBACK_SUBACCOUNT_RANGE; i++) {
      if (!usedIds.has(i)) {
        newId = i
        break
      }
    }

    if (newId >= 0) {
      options.push({
        id: newId,
        address: getSubAccountAddress(userAddress, newId),
        label: `New Position #${newId}`,
        hasPosition: false,
        isNew: true,
        debtSymbols: [],
        totalDebtUsd: 0,
        totalCollateralUsd: 0,
        healthFactor: null,
      })
    }

    return options
  }, [userAddress, activeSubAccounts, sourceSubAccountId, enrichmentMap, getPositionName])

  // Max transferable is the full current balance
  const maxTransferable = currentBalance

  // Create token
  const token = useMemo(() => {
    if (!assetSymbol) return undefined
    return new Token({
      symbol: TokenSymbol(assetSymbol),
      name: assetSymbol,
      decimals: assetDecimals,
      address: CheckedAddress(underlyingAsset),
      unitPriceUsd: String(priceUSD),
      logoURI,
    })
  }, [assetSymbol, assetDecimals, underlyingAsset, priceUSD, logoURI])

  // Check if this is a max transfer
  const isMaxTransfer = useMemo(() => {
    if (!debouncedTransferAmount || Number(debouncedTransferAmount) <= 0) return false
    return Number(debouncedTransferAmount) >= currentBalance * 0.999
  }, [debouncedTransferAmount, currentBalance])

  // Convert transfer amount from underlying assets to vault shares
  // Uses BigNumber to avoid scientific notation issues with Number.toString()
  const transferSharesValue = useMemo(() => {
    if (!debouncedTransferAmount || Number(debouncedTransferAmount) <= 0 || !userSharesRaw) return '0'
    if (isMaxTransfer) return userSharesRaw.toString()

    try {
      const amount = new BigNumber(debouncedTransferAmount)
      const balance = new BigNumber(currentBalance)
      if (balance.isZero()) return '0'
      const shares = amount
        .dividedBy(balance)
        .multipliedBy(userSharesRaw.toString())
        .integerValue(BigNumber.ROUND_FLOOR)
      return shares.isPositive() ? shares.toFixed(0) : '0'
    } catch {
      return '0'
    }
  }, [debouncedTransferAmount, userSharesRaw, currentBalance, isMaxTransfer])

  // Destination sub-account address
  const destSubAccountAddress = useMemo(() => {
    if (!userAddress || !selectedDestination) return undefined
    return getSubAccountAddress(userAddress, selectedDestination.id)
  }, [userAddress, selectedDestination])

  // Transfer amount validation
  const transferAmountNum = Number(debouncedTransferAmount) || 0
  const wouldBreachHF = sourceHasDebt && transferAmountRaw > 0n && !isSimulatingSourceHF && newSourceHF <= 1.25
  const canTransfer =
    transferAmountNum > 0 &&
    transferAmountNum <= currentBalance &&
    !!selectedDestination &&
    !isDebouncing &&
    !wouldBreachHF &&
    !(sourceHasDebt && isSimulatingSourceHF)

  const transferValueUSD = (Number(transferAmount) || 0) * priceUSD

  // Build transfer transactions for EVC batch simulation
  const transactions = useMemo((): TransactionItems => {
    if (!sourceSubAccountAddress || !destSubAccountAddress || !debouncedTransferAmount || Number(debouncedTransferAmount) <= 0) return []
    if (transferSharesValue === '0' && !isMaxTransfer) return []

    const asset = {
      address: underlyingAsset,
      name: assetSymbol,
      symbol: assetSymbol,
      decimals: assetDecimals,
    }

    const transferTx: TransferTransactionItem = {
      label: TransactionLabel.TRANSFER,
      from: sourceSubAccountAddress,
      to: destSubAccountAddress,
      asset,
      vault: vaultAddress,
      value: debouncedTransferAmount,
      sharesValue: transferSharesValue,
      isMax: isMaxTransfer,
    }

    return [transferTx]
  }, [
    sourceSubAccountAddress,
    destSubAccountAddress,
    debouncedTransferAmount,
    transferSharesValue,
    isMaxTransfer,
    underlyingAsset,
    assetSymbol,
    assetDecimals,
    vaultAddress,
  ])

  // Simulate the EVC batch
  const {
    data: simulationData,
    isLoading: isSimulating,
    isFetching: isSimulationFetching,
    error: simulationError,
  } = useSimulateEVCBatchCall({
    config,
    transactions,
    query: {
      enabled: !!userAddress && transactions.length > 0 && canTransfer && !wouldBreachHF,
      retry: false,
    },
  })

  // Write contract
  const { writeContract, isPending: isWriting, error: writeError, data: txHash, reset: resetWrite } = useWriteContract()

  // Wait for receipt
  const {
    isLoading: isWaiting,
    isSuccess: isConfirmed,
    error: waitError,
  } = useWaitForTransactionReceiptUniversal({ hash: txHash })

  // Execute the batch
  const handleExecute = useCallback(() => {
    if (writeError) {
      resetWrite()
    }
    if (simulationData?.request) {
      writeContract(simulationData.request as any)
    }
  }, [simulationData, writeContract, writeError, resetWrite])

  const transferTimeout = useActionTimeout({
    timeoutMs: 15000,
    isLoading: isWriting || isWaiting || isSimulating || isSimulationFetching,
    isSuccess: isSuccess,
    error: writeError || waitError || simulationError,
  })

  const handleExecuteWithTimeout = useCallback(() => {
    transferTimeout.handleActionClick(handleExecute)
  }, [transferTimeout, handleExecute])

  // Filter out user rejection errors
  const displayError = useMemo(() => {
    if (transferTimeout.isTimedOut) {
      return transferTimeout.displayError
    }
    const errors = [simulationError, writeError, waitError].filter(Boolean)
    return (errors.find((err) => !isUserRejection(err as Error)) as Error) || null
  }, [simulationError, writeError, waitError, transferTimeout.isTimedOut, transferTimeout.displayError])

  const invalidateSubaccounts = useSubaccountStore((state) => state.invalidate)

  // Mark as success and invalidate caches when confirmed
  useEffect(() => {
    if (isConfirmed && !isSuccess) {
      setIsSuccess(true)
      invalidateBalancesCache({ queryClient, chainId })
      invalidateSubaccounts()
    }
  }, [isConfirmed, isSuccess, queryClient, chainId, invalidateSubaccounts])

  return {
    token,
    transferAmount,
    setTransferAmount,
    maxTransferable,
    selectedDestination,
    setSelectedDestination,
    destinations,
    isDestinationsLoading: isControllersLoading || isCollateralStateLoading,
    canTransfer,
    transferValueUSD,
    sourceSubAccountLabel,
    isDebouncing,
    isSimulating: isSimulating || isSimulationFetching,
    isWriting,
    isWaiting,
    isConfirmed,
    hasSimulationData: !!simulationData,
    displayError,
    simulationError: simulationError ?? null,
    handleExecute: handleExecuteWithTimeout,
    isSuccess,
    isTimedOut: transferTimeout.isTimedOut,
    currentSourceHF,
    newSourceHF,
    isSimulatingSourceHF,
    sourceHasDebt,
  }
}
